import React, { useState, useEffect } from 'react';
import { ProjectRow, CapitalProject } from '../../lib/types';
import { useAuth } from '../../hooks/useAuth';
import styles from './project-capital.module.css';

interface ProjectWithCapital extends ProjectRow {
  capitalData?: CapitalProject;
}

interface ModalState {
  isOpen: boolean;
  projectId: string | null;
  isEditing: boolean;
  formData: {
    projectId: string;
    isCapital: boolean;
    financeCostCenter: string;
    financeBusinessUnit: string;
    budgetAmount: string;
    budgetCurrency: string;
  };
}

interface SummaryStats {
  totalProjects: number;
  capitalProjectsCount: number;
  totalBudgetedAmount: number;
}

export default function ProjectCapital() {
  const { user } = useAuth();
  const [projects, setProjects] = useState<ProjectWithCapital[]>([]);
  const [capitalProjects, setCapitalProjects] = useState<Map<string, CapitalProject>>(new Map());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCapitalOnly, setFilterCapitalOnly] = useState(false);
  const [summaryStats, setSummaryStats] = useState<SummaryStats>({
    totalProjects: 0,
    capitalProjectsCount: 0,
    totalBudgetedAmount: 0,
  });

  const [modal, setModal] = useState<ModalState>({
    isOpen: false,
    projectId: null,
    isEditing: false,
    formData: {
      projectId: '',
      isCapital: false,
      financeCostCenter: '',
      financeBusinessUnit: '',
      budgetAmount: '',
      budgetCurrency: 'USD',
    },
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch all projects
      const projectsRes = await fetch('/projects');
      const projectsData = await projectsRes.json();
      const projectsList = projectsData.data || [];

      // Fetch all capital projects
      const capitalRes = await fetch('/capital-projects');
      const capitalData = capitalRes.json ? await capitalRes.json() : { data: [] };
      const capitalList = capitalData.data || [];

      // Create a map of capital projects by ProjectId
      const capitalMap = new Map<string, CapitalProject>();
      capitalList.forEach((cp: CapitalProject) => {
        capitalMap.set(cp.ProjectId, cp);
      });

      setProjects(projectsList);
      setCapitalProjects(capitalMap);
      updateSummaryStats(projectsList, capitalMap);
    } catch (err: any) {
      console.error('Failed to fetch data:', err);
      setError('Failed to load projects. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateSummaryStats = (projectsList: ProjectRow[], capitalMap: Map<string, CapitalProject>) => {
    let totalBudget = 0;
    capitalMap.forEach((cp) => {
      if (cp.IsCapital && cp.BudgetAmount) {
        totalBudget += cp.BudgetAmount;
      }
    });

    setSummaryStats({
      totalProjects: projectsList.length,
      capitalProjectsCount: Array.from(capitalMap.values()).filter(cp => cp.IsCapital).length,
      totalBudgetedAmount: totalBudget,
    });
  };

  const openModal = (projectId: string, isEditing: boolean = false) => {
    const capital = capitalProjects.get(projectId);

    if (isEditing && capital) {
      setModal({
        isOpen: true,
        projectId,
        isEditing: true,
        formData: {
          projectId,
          isCapital: capital.IsCapital || false,
          financeCostCenter: capital.FinanceCostCenter || '',
          financeBusinessUnit: capital.FinanceBusinessUnit || '',
          budgetAmount: capital.BudgetAmount ? String(capital.BudgetAmount) : '',
          budgetCurrency: capital.BudgetCurrency || 'USD',
        },
      });
    } else {
      setModal({
        isOpen: true,
        projectId,
        isEditing: false,
        formData: {
          projectId,
          isCapital: true,
          financeCostCenter: '',
          financeBusinessUnit: '',
          budgetAmount: '',
          budgetCurrency: 'USD',
        },
      });
    }
  };

  const closeModal = () => {
    setModal({
      isOpen: false,
      projectId: null,
      isEditing: false,
      formData: {
        projectId: '',
        isCapital: false,
        financeCostCenter: '',
        financeBusinessUnit: '',
        budgetAmount: '',
        budgetCurrency: 'USD',
      },
    });
    setSuccessMessage('');
  };

  const handleFormChange = (field: string, value: any) => {
    setModal(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        [field]: value,
      },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
      if (modal.formData.isCapital) {
        if (!modal.formData.financeCostCenter.trim()) {
          throw new Error('Finance Cost Center is required');
        }
        if (!modal.formData.financeBusinessUnit.trim()) {
          throw new Error('Finance Business Unit is required');
        }
        if (!modal.formData.budgetAmount || isNaN(Number(modal.formData.budgetAmount))) {
          throw new Error('Budget Amount must be a valid number');
        }
      }

      const payload = {
        projectId: modal.formData.projectId,
        isCapital: modal.formData.isCapital,
        financeCostCenter: modal.formData.isCapital ? modal.formData.financeCostCenter : null,
        financeBusinessUnit: modal.formData.isCapital ? modal.formData.financeBusinessUnit : null,
        budgetAmount: modal.formData.isCapital ? Number(modal.formData.budgetAmount) : null,
        budgetCurrency: modal.formData.isCapital ? modal.formData.budgetCurrency : null,
      };

      const res = await fetch('/capital-projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const responseData = await res.json();
      if (!responseData.success) {
        throw new Error(responseData.error || 'Failed to save capital project data');
      }

      setSuccessMessage(
        modal.isEditing
          ? 'Capital project data updated successfully!'
          : 'Project flagged as capital successfully!'
      );

      // Refresh data
      await new Promise(resolve => setTimeout(resolve, 500));
      await fetchData();
      closeModal();
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveCapitalFlag = async (projectId: string) => {
    if (!window.confirm('Are you sure you want to remove the capital flag from this project?')) {
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const res = await fetch('/capital-projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId,
          isCapital: false,
        }),
      });

      const responseData = await res.json();
      if (!responseData.success) {
        throw new Error(responseData.error || 'Failed to remove capital flag');
      }

      setSuccessMessage('Capital flag removed successfully!');
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredProjects = projects.filter(project => {
    const capital = capitalProjects.get(project.ProjectId);
    const matchesSearch =
      project.Title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.ProjectId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = !filterCapitalOnly || (capital && capital.IsCapital);
    return matchesSearch && matchesFilter;
  });

  if (!user) {
    return <div className={styles.error}>Not authenticated</div>;
  }

  return (
    <div className={styles.container}>
        <header className={styles.header}>
          <h1>Capital Projects Management</h1>
          <p className={styles.subtitle}>Flag and manage finance metadata for capital projects</p>
        </header>

        {/* Summary Stats */}
        <div className={styles.summaryGrid}>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>Total Projects</div>
            <div className={styles.summaryValue}>{summaryStats.totalProjects}</div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>Capital Projects</div>
            <div className={styles.summaryValue}>{summaryStats.capitalProjectsCount}</div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>Total Budgeted</div>
            <div className={styles.summaryValue}>
              ${summaryStats.totalBudgetedAmount.toLocaleString('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className={styles.alertError}>
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className={styles.alertSuccess}>
            <strong>Success:</strong> {successMessage}
          </div>
        )}

        {/* Filters and Search */}
        <div className={styles.filterBar}>
          <input
            type="text"
            placeholder="Search projects by name or ID..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
          <label className={styles.filterLabel}>
            <input
              type="checkbox"
              checked={filterCapitalOnly}
              onChange={e => setFilterCapitalOnly(e.target.checked)}
              className={styles.filterCheckbox}
            />
            Capital Projects Only
          </label>
        </div>

        {/* Projects Table */}
        <div className={styles.tableWrapper}>
          {loading ? (
            <div className={styles.loadingMessage}>Loading projects...</div>
          ) : filteredProjects.length === 0 ? (
            <div className={styles.emptyMessage}>
              {projects.length === 0
                ? 'No projects found. Create a project to get started.'
                : 'No projects match your search criteria.'}
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Project ID</th>
                  <th>Project Name</th>
                  <th>Status</th>
                  <th>Capital?</th>
                  <th>Cost Center</th>
                  <th>Business Unit</th>
                  <th>Budget Amount</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.map(project => {
                  const capital = capitalProjects.get(project.ProjectId);
                  const isCapital = capital?.IsCapital || false;

                  return (
                    <tr key={project.ProjectId} className={isCapital ? styles.capitalRow : ''}>
                      <td className={styles.projectId}>{project.ProjectId}</td>
                      <td className={styles.projectName}>
                        <a href={`#/projects/${project.ProjectId}`} className={styles.projectLink}>
                          {project.Title}
                        </a>
                      </td>
                      <td>
                        <span className={`${styles.statusBadge} ${styles[`status${project.Status}`]}`}>
                          {project.Status}
                        </span>
                      </td>
                      <td className={styles.capitalColumn}>
                        {isCapital ? (
                          <span className={styles.capitalYes}>✓ Yes</span>
                        ) : (
                          <span className={styles.capitalNo}>—</span>
                        )}
                      </td>
                      <td>{capital?.FinanceCostCenter || '—'}</td>
                      <td>{capital?.FinanceBusinessUnit || '—'}</td>
                      <td>
                        {capital?.BudgetAmount
                          ? `${capital.BudgetCurrency || 'USD'} ${capital.BudgetAmount.toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}`
                          : '—'}
                      </td>
                      <td className={styles.actionsColumn}>
                        {!isCapital ? (
                          <button
                            className={`${styles.button} ${styles.buttonPrimary}`}
                            onClick={() => openModal(project.ProjectId, false)}
                            title="Flag this project as capital"
                          >
                            Flag as Capital
                          </button>
                        ) : (
                          <>
                            <button
                              className={`${styles.button} ${styles.buttonSecondary}`}
                              onClick={() => openModal(project.ProjectId, true)}
                              title="Edit capital project details"
                            >
                              Edit
                            </button>
                            <button
                              className={`${styles.button} ${styles.buttonDanger}`}
                              onClick={() => handleRemoveCapitalFlag(project.ProjectId)}
                              title="Remove capital flag"
                              disabled={submitting}
                            >
                              Remove
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Modal */}
        {modal.isOpen && (
          <div className={styles.modalOverlay} onClick={closeModal}>
            <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
              <header className={styles.modalHeader}>
                <h2>
                  {modal.isEditing ? 'Edit Capital Project' : 'Flag Project as Capital'}
                </h2>
                <button
                  className={styles.modalCloseButton}
                  onClick={closeModal}
                  aria-label="Close modal"
                >
                  ×
                </button>
              </header>

              <form onSubmit={handleSubmit} className={styles.form}>
                {/* Project ID - Read Only */}
                <div className={styles.formGroup}>
                  <label htmlFor="projectId" className={styles.label}>
                    Project ID
                  </label>
                  <input
                    type="text"
                    id="projectId"
                    value={modal.formData.projectId}
                    disabled
                    className={styles.inputDisabled}
                  />
                </div>

                {/* Is Capital Toggle */}
                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={modal.formData.isCapital}
                      onChange={e => handleFormChange('isCapital', e.target.checked)}
                      className={styles.checkbox}
                    />
                    <span>This is a capital project</span>
                  </label>
                </div>

                {/* Finance Fields - Show only if isCapital is true */}
                {modal.formData.isCapital && (
                  <fieldset className={styles.fieldset}>
                    <legend className={styles.legend}>Finance Information</legend>

                    <div className={styles.formGroup}>
                      <label htmlFor="costCenter" className={styles.label}>
                        Finance Cost Center <span className={styles.required}>*</span>
                      </label>
                      <input
                        type="text"
                        id="costCenter"
                        value={modal.formData.financeCostCenter}
                        onChange={e => handleFormChange('financeCostCenter', e.target.value)}
                        placeholder="e.g., CC-2026-001"
                        className={styles.input}
                      />
                    </div>

                    <div className={styles.formGroup}>
                      <label htmlFor="businessUnit" className={styles.label}>
                        Finance Business Unit <span className={styles.required}>*</span>
                      </label>
                      <input
                        type="text"
                        id="businessUnit"
                        value={modal.formData.financeBusinessUnit}
                        onChange={e => handleFormChange('financeBusinessUnit', e.target.value)}
                        placeholder="e.g., Platform Engineering"
                        className={styles.input}
                      />
                    </div>

                    <div className={styles.formRow}>
                      <div className={styles.formGroup}>
                        <label htmlFor="budgetAmount" className={styles.label}>
                          Budget Amount <span className={styles.required}>*</span>
                        </label>
                        <input
                          type="number"
                          id="budgetAmount"
                          value={modal.formData.budgetAmount}
                          onChange={e => handleFormChange('budgetAmount', e.target.value)}
                          placeholder="e.g., 250000.00"
                          step="0.01"
                          min="0"
                          className={styles.input}
                        />
                      </div>

                      <div className={styles.formGroup}>
                        <label htmlFor="currency" className={styles.label}>
                          Currency
                        </label>
                        <select
                          id="currency"
                          value={modal.formData.budgetCurrency}
                          onChange={e => handleFormChange('budgetCurrency', e.target.value)}
                          className={styles.input}
                        >
                          <option value="USD">USD</option>
                          <option value="EUR">EUR</option>
                          <option value="GBP">GBP</option>
                          <option value="CAD">CAD</option>
                          <option value="AUD">AUD</option>
                        </select>
                      </div>
                    </div>
                  </fieldset>
                )}

                {/* Form Actions */}
                <div className={styles.formActions}>
                  <button
                    type="button"
                    onClick={closeModal}
                    className={`${styles.button} ${styles.buttonSecondary}`}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`${styles.button} ${styles.buttonPrimary}`}
                    disabled={submitting}
                  >
                    {submitting
                      ? 'Saving...'
                      : modal.isEditing
                        ? 'Update Capital Project'
                        : 'Flag as Capital'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }
